import Button from 'shared/Buttons/Button/Button'
import TextField from 'shared/Forms/TextField'
import Modal from 'shared/Modal'
import ModalHeader from 'shared/Modal/ModalHeader'
import eventBus from 'services/eventBus'
import css from './ChangeRoomSettings.module.css'
import TextInput from 'shared/Inputs/TextInput'
import useHttpLoader from 'shared/hooks/useHttpLoader'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Select, { SelectOption } from 'shared/Inputs/Select'
import protectionsApi from 'screens/Protection/protection.api'
import protectionTemplateApi from 'screens/Admin/ProtectionTemplates/protection.api'
import { IProtectionTemplate } from 'screens/Admin/ProtectionTemplates/protection.types'
import { useRecoilValue } from 'recoil'
import { activeRoomAtom } from 'screens/Protection/protection.atom'
import MultiSelect from 'shared/Inputs/Select/MultiSelect'
import NumberInput from 'shared/Inputs/NumberInput'
import DateInput from 'shared/Inputs/DateInput'
import protectionDayAtom from '../../../../protectionDay.atom'

type Mode = 'create' | 'update'

type Props = {
  type: Mode
  onClose: () => void
  onRoomCreated: (room: any) => void
}

const ChangeRoomSettingsModal = ({ onClose, onRoomCreated, type }: Props) => {
  const [nameRoom, setNameRoom] = useState('')
  const [roomLink, setRoomLink] = useState('')
  const [minutesForTeam, setMinutesForTeam] = useState<number>(0)
  const [startsAt, setStartsAt] = useState('')
  const [startsTime, setStartsTime] = useState('')

  const [experts, setExperts] = useState<SelectOption[]>([])
  const [selectedExperts, setSelectedExperts] = useState<SelectOption[]>([])

  const [templates, setTemplates] = useState<SelectOption[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<IProtectionTemplate['id'] | null>(null)

  const params = useParams()
  const { wait } = useHttpLoader()
  const activeRoom = useRecoilValue(activeRoomAtom)
  const protectionDay = useRecoilValue(protectionDayAtom)

  useEffect(() => {
    if (!params.id) return

    wait(protectionTemplateApi.getProtectionTemplates(), (resp) => {
      if (resp.status === 'success') {
        const templatesOptions =
          resp.body.items?.map((template) => ({
            value: template.id,
            label: template.name,
          })) ?? []

        setTemplates(templatesOptions)
      }
    })

    wait(protectionsApi.getExperts({ limit: '1000' }), (resp) => {
      if (resp.status === 'success') {
        const expertsOptions: SelectOption[] = resp.body.items.map((expert) => ({
          value: expert.id,
          label: expert.info.name,
        }))

        setExperts(expertsOptions)
      }
    })
  }, [params.id])

  useEffect(() => {
    if (!protectionDay?.defence?.startsAt) return

    const date = new Date(protectionDay.defence.startsAt).toISOString().slice(0, 10)

    setStartsAt(date)
  }, [protectionDay])

  useEffect(() => {
    if (type === 'update' && activeRoom) {
      setNameRoom(activeRoom.name)
      setSelectedTemplate(activeRoom.ratingTemplateId)
      setRoomLink(activeRoom.roomLink ?? '')
      setMinutesForTeam(activeRoom.minutesForTeam ?? 0)

      if (activeRoom.startsAt) {
        const dateObj = new Date(activeRoom.startsAt)

        const date = dateObj.toISOString().slice(0, 10)
        const time = dateObj.toTimeString().slice(0, 5)

        setStartsAt(date)
        setStartsTime(time)
      }

      setSelectedExperts(
        activeRoom.experts?.map((expert) => ({
          value: expert.id,
          label: expert.info.name,
        })) ?? []
      )
    } else if (type === 'create') {
      setNameRoom('')
      setRoomLink('')
      setMinutesForTeam(0)
      setSelectedTemplate(null)
      setSelectedExperts([])
      setStartsTime('')
    }
  }, [activeRoom, type])

  const handleNameChange = (val: string) => {
    setNameRoom(val)
  }

  const handleLinkChange = (val: string) => {
    setRoomLink(val)
  }

  const handleMinutesForTeamChange = (val: number) => {
    setMinutesForTeam(val)
  }

  const handleStartsTimeChange = (time: string) => {
    setStartsTime(time)
  }

  const handleSave = () => {
    if (!nameRoom.trim() || !selectedTemplate || !params.id) {
      eventBus.pub('ntf.message', {
        type: 'error',
        message: 'Заполните все обязательные поля',
      })

      return
    }

    const experts = selectedExperts.map((expert) => expert.value)

    const startsAtValue = new Date(`${startsAt}T${startsTime}:00`)

    if (type === 'create') {
      wait(
        protectionsApi.createRoom({
          name: nameRoom,
          ratingTemplateId: selectedTemplate,
          defenceId: params.id,
          expertIds: experts,
          link: roomLink,
          minutesForTeam: Number(minutesForTeam),
          startsAt: startsAtValue,
        }),
        (resp) => {
          if (resp.status !== 'success') {
            eventBus.pub('ntf.message', {
              type: 'error',
              message: resp.message || 'Ошибка при создании комнаты',
            })
            return
          }

          const room = resp.body.room

          eventBus.pub('ntf.message', {
            type: 'info',
            message: 'Комната создана',
          })

          onRoomCreated(room)
          onClose()
        }
      )
    } else {
      if (!activeRoom?.id) return

      wait(
        protectionsApi.updateRoom(activeRoom.id, {
          name: nameRoom,
          ratingTemplateId: selectedTemplate,
          defenceId: params.id,
          expertIds: experts,
          link: roomLink,
          minutesForTeam: Number(minutesForTeam),
          startsAt: startsAtValue,
        }),
        (resp) => {
          if (resp.status !== 'success') {
            eventBus.pub('ntf.message', {
              type: 'error',
              message: resp.message || 'Ошибка при обновлении комнаты',
            })
            return
          }

          const room = resp.body.room

          onRoomCreated(room)

          eventBus.pub('ntf.message', {
            type: 'info',
            message: 'Комната обновлена',
          })

          onClose()
        }
      )
    }
  }

  return (
    <Modal onClose={onClose} size='md' shouldAnimate={false}>
      <ModalHeader onClose={onClose}>Настройки комнаты</ModalHeader>

      <div className={`modal-content flex-lines ${css.content}`}>
        <TextField header='Название комнаты'>
          <TextInput onChange={handleNameChange} value={nameRoom} name='name' placeholder='Комната 1' />
        </TextField>

        <TextField header='Время на команду (мин)'>
          <NumberInput onChange={handleMinutesForTeamChange} value={minutesForTeam} name='minutesForTeam' />
        </TextField>

        <TextField header='Дата начала'>
          <DateInput name='startsAt' date={startsAt} onChange={() => {}} disabled />
        </TextField>

        <TextField header='Время начала'>
          <TextInput type='time' name='startsTime' value={startsTime} onChange={handleStartsTimeChange} />
        </TextField>

        <TextField header='Эксперты'>
          <MultiSelect
            maxHeight={160}
            name='experts'
            options={experts}
            selectedOptions={selectedExperts}
            onChange={(values) => {
              setSelectedExperts(values)
            }}
            placeholder='Выберите экспертов'
          />
        </TextField>

        <TextField header='Шаблоны'>
          <Select
            name='template'
            options={templates}
            selectedOption={templates.find((item) => item.value === selectedTemplate)}
            onChange={(value) => {
              setSelectedTemplate(value)
            }}
          />
        </TextField>

        <TextField header='Ссылка'>
          <TextInput
            onChange={handleLinkChange}
            value={roomLink}
            name='link'
            placeholder='https://telemost.yandex.ru/...'
          />
        </TextField>
      </div>

      <div className={css.footer}>
        <div className={`inline-flex-gap ${css.footerActions}`}>
          <Button theme='primary' className={css.button} onClick={handleSave}>
            Сохранить
          </Button>

          <Button theme='grey-outline' className={css.button} onClick={onClose}>
            Отмена
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default ChangeRoomSettingsModal
